<template lang="html">
  <div class="prt-transaction">
    <div class="card" @touchstart="swipeStart" :style="{left: `${cardPositionX}px`, transition: enableTransition ? 'left 200ms ease' : ''}" tabindex="-1" @touchend="swipeEnd" @touchmove="swipeUpdate" @mousedown="swipeStart">
      <prt-icon class="icon" :icon="icon" />
      <section class="label-wrapper">
        <h1>{{description}}</h1>
        <span>{{transformedTimestamp}}</span>
      </section>
      <span class="amount" :class="{negative: amount < 0}">{{amount | transformAmount}}</span>
    </div>
    <transition name="zoom">
      <button aria-label="Delete" class="delete-button" v-if="showDelete" @click="$emit('delete')">
        <prt-icon icon="delete" />
      </button>
    </transition>
  </div>
</template>

<script>
import { distanceInWordsToNow } from 'date-fns';

import PrtIcon from './PrtIcon.vue';

export default {
  beforeDestroy() {
    window.removeEventListener('click', this.handleBlur);
    window.removeEventListener('touchstart', this.handleBlur);
  },
  components: {
    PrtIcon,
  },
  computed: {
    transformedTimestamp() {
      return distanceInWordsToNow(this.timestamp, { includeSeconds: true, addSuffix: true });
    },
  },
  mounted() {
    window.addEventListener('click', this.handleBlur);
    window.addEventListener('touchstart', this.handleBlur);
  },
  data() {
    return {
      animationFrame: false,
      cardPositionX: 0,
      cardStartPositionX: 0,
      enableTransition: false,
      isScroll: null,
      mousepressed: false,
      showDelete: false,
      touchEndX: false,
      touchStartX: false,
      touchStartY: false,
    };
  },
  methods: {
    handleBlur(e) {
      if (!this.$el.contains(e.target)) this.resetSwipe();
    },
    handleSwipeEnd() {
      const threshold = Math.min(window.innerWidth, 1200) / 2;
      const thresholdButton = 26;
      window.cancelAnimationFrame(this.animationFrame);
      this.enableTransition = true;
      this.isScroll = null;
      if (this.cardPositionX >= thresholdButton && this.cardPositionX < threshold) {
        this.cardPositionX = 52;
        this.showDelete = true;
      } else if (this.cardPositionX >= threshold) {
        this.$emit('delete');
        this.cardPositionX = window.innerWidth;
        this.showDelete = false;
      } else {
        this.cardPositionX = 0;
        this.showDelete = false;
      }
    },
    resetSwipe() {
      if (this.showDelete || this.cardPositionX !== 0) {
        this.cardPositionX = 0;
        this.showDelete = false;
      }
    },
    swiping(e) {
      const currentX = e.type === 'touchmove' ? e.changedTouches[0].screenX : e.screenX;
      const currentY = e.type === 'touchmove' ? e.changedTouches[0].screenY : e.screenY;
      const distance = currentX - this.touchStartX;
      const distanceY = currentY - this.touchStartY;
      const limitLeft = 0;
      const limitRight = window.innerWidth;

      if (Math.abs(distanceY) >= Math.abs(distance) && this.isScroll === null) this.isScroll = true;
      else if (this.isScroll === null) this.isScroll = false;

      if (this.isScroll) return;

      if (this.cardPositionX >= limitLeft && this.cardPositionX <= limitRight && (e.type === 'touchmove' || this.mousepressed)) {
        if (distance >= 0) {
          this.cardPositionX = Math.min(this.cardStartPositionX + distance, limitRight);
        } else this.cardPositionX = Math.max(this.cardStartPositionX + distance, limitLeft);
      }
    },
    swipeStart(e) {
      this.touchStartX = e.type === 'touchstart' ? e.changedTouches[0].screenX : e.screenX;
      this.touchStartY = e.type === 'touchstart' ? e.changedTouches[0].screenY : e.screenY;
      this.cardStartPositionX = this.cardPositionX;
      if (this.enableTransition) this.enableTransition = false;
      if (e.type === 'mousedown') {
        this.mousepressed = true;
        window.addEventListener('mousemove', this.swipeUpdate, false);
        window.addEventListener('mouseup', this.swipeEnd, false);
      } else e.currentTarget.focus();
    },
    swipeEnd(e) {
      this.touchEndX = e.type === 'touchend' ? e.changedTouches[0].screenX : e.screenX;
      if (e.type === 'mouseup') {
        this.mousepressed = false;
        window.removeEventListener('mousemove', this.swipeUpdate, false);
        window.removeEventListener('mouseup', this.swipeEnd, false);
      }
      this.handleSwipeEnd();
    },
    swipeUpdate(e) {
      this.animationFrame = window.requestAnimationFrame(() => this.swiping(e));
    },
  },
  props: {
    amount: {
      type: Number,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      required: true,
    },
    timestamp: {
      type: Number,
      required: true,
    },
  },
};
</script>

<style lang="stylus" scoped>
  @require '../../styles/colors'

  .prt-transaction
    width: calc(100% - 8px)
    height: 48px
    margin: 4px
    position: relative

    .card
      background-color: white
      box-shadow: 0 2px 4px black-12
      border-radius: 3px
      display: flex
      align-items: center
      height: 100%
      position: relative
      z-index: 2
      left: 0px
      outline: none
      overflow-y: hidden

      .icon
        margin: 0 12px
        flex-shrink: 0
        color: positive

      .label-wrapper
        width: 100%
        white-space: nowrap
        line-height: 1.05
        overflow: hidden

        h1
          font-size: 16px
          font-weight: normal
          margin: 0
          text-overflow: ellipsis
          overflow: hidden

        span
          font-size: 10px
          font-weight: bold
          letter-spacing: 1px
          text-transform: uppercase
          color: black-38
          overflow: hidden
          text-overflow: ellipsis

      .amount
        white-space: nowrap
        color: positive
        margin-right: 12px
        font-weight: bold
        flex-shrink: 0

        &.negative
          color: negative

    .delete-button
      border: none
      outline: none
      background-color: negative
      color: white
      box-shadow: 0 2px 4px black-12
      padding: 11px
      width: 40px
      height: @width
      border-radius: (@width / 2)px
      position: absolute
      left: 4px
      top: 4px

      &:active
        background-color: lighten(negative, 30%)
        transform: translateY(2px)

      &.zoom-enter-active,
      &.zoom-leave-active
        transition: transform 200ms ease

        .prt-icon
          transition: transform 100ms ease

      &.zoom-enter,
      &.zoom-leave-to
        transform: scale(0)

        .prt-icon
          transform: scale(0)
</style>
